import type { CatalogProductRow } from './catalogTypes';
import { effectiveCatalogUnitCost } from './comboMetrics';
import { formatQtyCompact } from '@shared/quantity';

/** Tone alineado a StatusPillTone / KPI warn — sin depender de UI packages. */
export type CatalogStockSignalTone = 'neutral' | 'warning' | 'danger' | 'success';

export type CatalogStockSignal = {
  label: string;
  tone: CatalogStockSignalTone;
};

/** Señal de inventario/costo unificada (lista, KPIs, filtros RAM). */
export function getCatalogProductStockSignal(product: CatalogProductRow): CatalogStockSignal {
  if (product.itemType === 'service' || product.isMisc) {
    return { label: 'Sin inventario', tone: 'neutral' };
  }

  if (product.isCombo) {
    const sell = product.comboSellableQty;
    if (sell == null) return { label: 'Kit · ver partes', tone: 'neutral' };
    if (product.comboReferenceComplete === false) return { label: 'Faltan partes', tone: 'danger' };
    if (sell <= 0) return { label: '~0 vendibles', tone: 'warning' };
    return {
      label: `~${formatQtyCompact(sell, product.unitOfMeasure)} vendibles`,
      tone: 'success',
    };
  }

  const stockQuantity = product.stockQuantity;
  const stockMinQuantity = product.stockMinQuantity ?? 0;
  const unitCost = effectiveCatalogUnitCost(product) ?? 0;
  const marginValue = product.price - unitCost;

  if (stockQuantity == null) return { label: 'Sin stock cargado', tone: 'warning' };
  if (stockQuantity <= 0) return { label: 'Sin stock', tone: 'danger' };
  const qtyLabel = formatQtyCompact(stockQuantity, product.unitOfMeasure);
  if (stockMinQuantity > 0 && stockQuantity <= stockMinQuantity) {
    return { label: `Bajo mínimo · ${qtyLabel}`, tone: 'warning' };
  }
  if (unitCost > 0 && marginValue < 0) return { label: 'Margen negativo', tone: 'danger' };
  if (unitCost <= 0) return { label: 'Sin costo', tone: 'warning' };
  return { label: `Stock ${qtyLabel}`, tone: 'success' };
}

export type CatalogProductStatusTone =
  | 'active'
  | 'paused'
  | 'service'
  | 'stock_empty'
  | 'stock_low';

export type CatalogProductStatus = {
  label: string;
  tone: CatalogProductStatusTone;
};

export function getCatalogProductStatus(product: CatalogProductRow): CatalogProductStatus {
  if (!product.isActive) return { label: 'Oculto', tone: 'paused' };
  if (product.itemType === 'service' || product.isMisc) return { label: 'Servicio', tone: 'service' };
  if (product.isCombo) {
    if (product.comboReferenceComplete === false) return { label: 'Faltan partes', tone: 'stock_empty' };
    const sell = product.comboSellableQty;
    if (sell != null && sell <= 0) return { label: 'Sin stock de partes', tone: 'stock_low' };
    return { label: 'Activo', tone: 'active' };
  }
  const qty = product.stockQuantity;
  if (qty != null && qty <= 0) return { label: 'Sin stock', tone: 'stock_empty' };
  const min = product.stockMinQuantity ?? 0;
  if (qty != null && min > 0 && qty <= min) return { label: 'Bajo mínimo', tone: 'stock_low' };
  return { label: 'Activo', tone: 'active' };
}

/** Mapper para chips TicketListRow. */
export function toTicketListRowTone(tone: CatalogProductStatusTone): 'ok' | 'warn' | 'bad' | 'muted' {
  switch (tone) {
    case 'paused':
      return 'muted';
    case 'stock_empty':
      return 'warn';
    case 'stock_low':
      return 'warn';
    default:
      return 'ok';
  }
}
